import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Fa from 'react-fontawesome';
import {
  reduce, find, delay, indexOf, first,
  without, concat, reject as lodashReject,
} from 'lodash';
import Autosuggest from 'react-autosuggest';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import {
  FormGroup, Tooltip, OverlayTrigger,
  ControlLabel,
} from 'react-bootstrap';
import Toggle from 'react-bootstrap-toggle';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styles from '../../../../components/search-form/styles.css';
import ConfirmButton from '../../../../components/confirm-button';


function escapeRegexCharacters(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function blurAll(parent = document.body) {
  const tmp = document.createElement('input');
  tmp.className = styles.hiddenInput;
  parent.appendChild(tmp);
  delay(() => {
    tmp.focus();
    parent.removeChild(tmp);
  }, 1);
}
const FadeAnimation = ({ children, ...props }) => (
  <CSSTransition
    {...props}
  >
    {children}
  </CSSTransition>
);
FadeAnimation.propTypes = {
  children: PropTypes.element,
};
FadeAnimation.defaultProps = {
  children: null,
};

class Field extends Component {
  constructor() {
    super();
    this.state = {
      suggestions: [],
      value: '',
      focus: false,
    };

    this.onSuggestionsFetchRequested = this.onSuggestionsFetchRequested.bind(this);
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.getSuggestionValue = this.getSuggestionValue.bind(this);
    this.renderSuggestion = this.renderSuggestion.bind(this);
    this.removeTag = this.removeTag.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onToggleBoolean = this.onToggleBoolean.bind(this);
    this.removeField = this.removeField.bind(this);
  }

  onChange(event, { newValue }) {
    this.setState({ value: newValue });
  }

  onFocus() {
    this.setState({ focus: true });
  }

  onBlur() {
    this.setState({ focus: false });
  }


  // Autosuggest will call this function every time you need to clear suggestions.
  onSuggestionsClearRequested() {
    this.setState({ suggestions: [] });
  }

  onSuggestionSelected(event, { suggestion }) {
    if (!suggestion.isEmpty && !suggestion.isHeader) {
      const { field } = this.props;
      const values = field.selectedValues || [];
      const { value } = suggestion;
      const exist = indexOf(values, value) >= 0;
      const newValues = exist ? without(values, value) : concat(values, value);
      this.props.onSelected(field, newValues);
    }
    this.setState({ value: '', focus: false });
    blurAll(this.element);
  }


  onSuggestionsFetchRequested({ value }) {
    this.setState({ suggestions: [] });
    const { field } = this.props;
    const escapedValue = escapeRegexCharacters(value.trim());
    const searching = escapedValue && escapedValue.length > 0;
    const regex = new RegExp(`${escapedValue}`, 'i');
    const selectedValues = field.selectedValues || [];

    const suggestions = reduce(field.values, (items, suggestion) => {
      const { label } = suggestion;
      const val = suggestion.value;
      if (indexOf(selectedValues, val) < 0) {
        if (searching && ((label || '').match(regex) || (val || '').match(regex))) {
          items.push(suggestion);
        } else if (!searching) {
          items.push(suggestion);
        }
      }
      return items;
    }, []);

    return new Promise((resolve) => {
      this.setState({ suggestions }, () => {
        resolve(suggestions);
      });
    });
  }

  onToggleBoolean() {
    const { field } = this.props;
    const { selectedValues } = field;
    const firstValue = first(selectedValues);
    const selected = firstValue === true;
    const newSelected = !selected;
    this.props.onSelected(field, [newSelected]);
  }

  getSuggestionValue(suggestion) {
    return suggestion.label;
  }

  removeTag(suggestion) {
    const { field } = this.props;
    const newValues = lodashReject(field.selectedValues, val => val === suggestion.value);
    this.props.onSelected(field, newValues);
    this.setState({ focus: false });
  }

  removeField() {
    this.props.onRemoveField(this.props.field);
  }

  renderSuggestion(suggestion, { query }) {
    const { label } = suggestion;
    if (suggestion.isEmpty) {
      return (
        <a href="" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} style={{ cursor: 'not-allowed' }}>
          <em className="text-muted">{suggestion.label}</em>
        </a>
      );
    }
    const matches = match(label, query);
    const parts = parse(label, matches).map((part, id) => ({ ...part, id }));
    return (
      <a href="" onClick={(e) => { e.preventDefault(); }}>
        <span>
          {parts.map((part) => {
            const className = part.highlight ? styles.tagHighlight : null;
            return (
              <span className={className} key={part.id}>
                {part.text}
              </span>
            );
          })}
        </span>
      </a>
    );
  }

  renderAutoComplete() {
    const {
      value, suggestions, focus,
    } = this.state;
    const { disabled, field } = this.props;
    const inputProps = {
      placeholder: 'search...',
      value,
      className: styles.tagInput,
      onChange: this.onChange,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      disabled,
    };
    const theme = {
      container: styles.autocomplete,
      suggestionsContainer: `dropdown open ${styles.suggestionsContainer}`,
      sectionContainer: `dropdown-menu ${styles.sectionContainer}`,
      suggestionsList: `dropdown-menu ${styles.suggestionsList}`,
      sectionTitle: 'dropdown-header',
      suggestion: '',
      suggestionFocused: 'active',
    };

    let count = 0;
    const tags = reduce(field.selectedValues, (items, val) => {
      const v = find(field.values, { value: val });
      if (v) {
        count += 1;
        items.push(v);
      }
      return items;
    }, []);
    const hasMore = count < field.values.length;

    const cls = [styles.tagInputField];
    if (focus) {
      cls.push(styles.tagInputFieldFocus);
    }
    return (
      <div
        ref={(el) => { this.element = el; }}
        className={`${cls.join(' ')}`}
      >
        <TransitionGroup className={styles.tagList}>
          {tags.map((tag) => {
            const tCls = [styles.tag, styles.mappingTag];
           
            if (disabled) {
              tCls.push(styles.disabledTag);
            }
            return (
              <FadeAnimation
                classNames="tag"
                timeout={500}
                key={tag._id}
              >
                <span className={`${tCls.join(' ')}`}>
                  <span className={styles.tagLabel}>{tag.label}</span>
                  <a
                    href=""
                    className={styles.tagTrash}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!disabled) {
                        this.removeTag(tag);
                      }
                    }}
                  >
                    <Fa name="times" />
                  </a>
                </span>
              </FadeAnimation>
            );
          })}
          {!disabled && hasMore &&
            <FadeAnimation
              classNames="tag"
              timeout={500}
            >
              <div className={styles.autocompleteWrapper}>
                <Autosuggest
                  theme={theme}
                  suggestions={suggestions}
                  onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                  shouldRenderSuggestions={() => true}
                  onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                  onSuggestionSelected={this.onSuggestionSelected}
                  getSuggestionValue={this.getSuggestionValue}
                  renderSuggestion={this.renderSuggestion}
                  inputProps={inputProps}
                />
                <Fa name="spinner" spin className={styles.spinner} />
              </div>
            </FadeAnimation>
          }
        </TransitionGroup>
      </div>
    );
  }

  renderToggle() {
    const { field: { selectedValues }, disabled } = this.props;
    const firstValue = first(selectedValues);
    const selected = firstValue === true;
    return (
      <Toggle
        onClick={this.onToggleBoolean}
        on={<span>Yes</span>}
        disabled={disabled}
        off={<span>No</span>}
        size="xs"
        onstyle="success"
        offstyle="danger"
        active={selected}
      />
    );
  }

  render() {
    const { field, disabled } = this.props;
    const { description } = field;
    const hasDescription = description && description.length > 0;
    let info = null;
    if (hasDescription) {
      const tip = (
        <Tooltip id={`field-${field._id}-tip`}>
          <div>{description}</div>
        </Tooltip>
      );
      info = (
        <OverlayTrigger placement="right" overlay={tip}><Fa name="info-circle" className="text-active" /></OverlayTrigger>
      );
    }

    return (
      <div className="well well-sm" style={{ marginBottom: '5px' }}>
        <FormGroup className="row">
          <ControlLabel className="col-xs-4 col-sm-3 col-md-2">
            {field.label} {info}
            <div><code>{field.name}</code></div>
          </ControlLabel>
          <div className="col-xs-7 col-sm-8 col-md-9">
            {field.type !== 'boolean' && this.renderAutoComplete()}
            {field.type === 'boolean' && this.renderToggle()}
          </div>
          <div className="col-xs-1">
            <div className="text-right">
              <ConfirmButton
                size="xs"
                disabled={disabled}
                confirmMessage="?"
                confirmStyle="link"
                onConfirm={this.removeField}
              >
                <Fa name="trash" />
              </ConfirmButton>
            </div>
          </div>
        </FormGroup>
      </div>
    );
  }
}


Field.propTypes = {
  field: PropTypes.shape({
    _id: PropTypes.string.isRequired,
  }).isRequired,
  disabled: PropTypes.bool,
  onSelected: PropTypes.func,
  onRemoveField: PropTypes.func,
};
Field.defaultProps = {
  disabled: false,
  onSelected: () => {},
  onRemoveField: () => {},
};

Field.contextTypes = {
};

export default Field;
